import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { ShieldAlert, History } from "lucide-react";

/**
 * Admin only — pilotage des feature flags centralisés serveur.
 * - Chaque changement exige un motif >= 10 caractères.
 * - Les flags critiques (auth, scope, signatures, dédoublonnage, finance) sont IRRÉVOCABLES.
 * - Journal append-only.
 */
export default function FeatureFlagsAdmin() {
  const [flags, setFlags] = useState({});
  const [configVersion, setConfigVersion] = useState(0);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { name, state }
  const [motif, setMotif] = useState("");
  const [serverTime, setServerTime] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, aud] = await Promise.all([
        api.get("/config/feature-flags"),
        api.get("/admin/feature-flags/audit?limit=50"),
      ]);
      setFlags(cfg.data.flags || {});
      setConfigVersion(cfg.data.config_version || 0);
      setServerTime(cfg.data.server_time || "");
      setAudit(aud.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de charger les flags");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    if (motif.trim().length < 10) {
      toast.error("Le motif doit faire au moins 10 caractères");
      return;
    }
    try {
      const r = await api.put(
        `/admin/feature-flags/${encodeURIComponent(editing.name)}`,
        { state: editing.state, motif: motif.trim() },
      );
      toast.success(
        `Flag ${editing.name} : ${r.data.old_state} → ${r.data.new_state} (v${r.data.config_version})`,
      );
      setEditing(null);
      setMotif("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Échec");
    }
  };

  if (loading) return <div className="p-8" data-testid="ff-admin-loading">Chargement…</div>;

  const stateVariant = (s) => s === "enabled" ? "bg-emerald-50 text-emerald-900 border-emerald-300"
                           : s === "hidden"  ? "bg-slate-100 text-slate-800 border-slate-300"
                           :                  "bg-amber-50 text-amber-900 border-amber-300";

  return (
    <div className="p-6 space-y-6" data-testid="ff-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Interrupteurs de fonctionnalités</h1>
          <div className="text-sm text-slate-600">
            Version de configuration : <strong data-testid="ff-config-version">{configVersion}</strong>
            {" · "}Serveur : <code>{serverTime}</code>
          </div>
        </div>
      </div>

      <Alert className="border-amber-300 bg-amber-50">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Limites irrévocables</AlertTitle>
        <AlertDescription className="text-sm">
          Un interrupteur <strong>suspend</strong> une fonctionnalité ; il <strong>ne remplace pas</strong>
          une sauvegarde ni un retour à un artefact logiciel testé. Les flags ne peuvent jamais désactiver
          l'authentification, le cloisonnement multi-tenant, les signatures, le dédoublonnage ou les
          contrôles financiers critiques. Délai de prise en compte côté client : jusqu'à 60 secondes.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader><CardTitle>Flags disponibles</CardTitle></CardHeader>
        <CardContent className="divide-y">
          {Object.entries(flags).map(([name, meta]) => (
            <div key={name} className="py-4 flex items-start justify-between gap-4" data-testid={`ff-row-${name}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <code className="font-mono text-sm">{name}</code>
                  <Badge variant="outline" className={stateVariant(meta.state)} data-testid={`ff-state-${name}`}>
                    {meta.state}
                  </Badge>
                </div>
                <div className="text-sm text-slate-700 mt-1">{meta.label}</div>
                <div className="text-xs text-slate-500 mt-1">{meta.description}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing({ name, state: meta.state })}
                data-testid={`ff-edit-${name}`}
              >
                Modifier
              </Button>
            </div>
          ))}
          {Object.keys(flags).length === 0 && (
            <div className="py-4 text-sm text-slate-500">Aucun flag visible pour ce rôle.</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" /> Journal append-only
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="ff-audit-table">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="p-2 text-left">Horodatage</th>
                <th className="p-2 text-left">Acteur</th>
                <th className="p-2 text-left">Flag</th>
                <th className="p-2 text-left">Transition</th>
                <th className="p-2 text-left">Motif</th>
                <th className="p-2 text-left">Version</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a, i) => (
                <tr key={i} className="border-b hover:bg-slate-50">
                  <td className="p-2 font-mono">{a.timestamp}</td>
                  <td className="p-2">{a.actor_email}</td>
                  <td className="p-2 font-mono">{a.flag}</td>
                  <td className="p-2">
                    <code>{a.old_state}</code> → <code>{a.new_state}</code>
                  </td>
                  <td className="p-2 max-w-xs truncate" title={a.motif}>{a.motif}</td>
                  <td className="p-2">v{a.config_version}</td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-slate-500 text-center">Aucun changement enregistré</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setMotif(""); } }}>
        <DialogContent data-testid="ff-edit-dialog" aria-describedby="ff-edit-dialog-desc">
          <DialogHeader>
            <DialogTitle>Modifier {editing?.name}</DialogTitle>
            <DialogDescription id="ff-edit-dialog-desc">
              Changement d'état du flag. Un motif de 10 caractères minimum est requis
              et sera consigné dans le journal append-only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>État cible</Label>
              <Select value={editing?.state} onValueChange={(v) => setEditing((e) => ({ ...e, state: v }))}>
                <SelectTrigger data-testid="ff-edit-state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="enabled">enabled — tout actif</SelectItem>
                  <SelectItem value="hidden">hidden — UI masquée, lecture OK</SelectItem>
                  <SelectItem value="new_ops_suspended">new_ops_suspended — écritures bloquées (423)</SelectItem>
                  <SelectItem value="read_only">read_only — alias (écritures 423)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motif obligatoire (≥ 10 caractères)</Label>
              <Input
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                placeholder="Ex: incident production, demande utilisateur, maintenance..."
                data-testid="ff-edit-motif"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setMotif(""); }}>Annuler</Button>
            <Button onClick={save} data-testid="ff-edit-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
